import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

export interface ResultHttpInterface {
  success: boolean;
  error: any;
  data: any;
}

export interface OAuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private accessToken: string | null = null;
  private tokenExpiration: number | null = null;
  private isRefreshing = false;
  private externalToken: string | null = null; // Token externo fornecido
  private tenantId: string | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  /**
   * Define um token externo (útil quando já temos um token válido)
   */
  setExternalToken(token: string): void {
    this.externalToken = token;
    this.accessToken = token;
    // Define expiração para 1 hora (3600 segundos)
    this.tokenExpiration = Date.now() + 3600 * 1000;
  }

  /**
   * Limpa o token externo
   */
  clearExternalToken(): void {
    this.externalToken = null;
    this.accessToken = null;
    this.tokenExpiration = null;
  }

  /**
   * Define o tenantId (empresa,filial) extraído do link RC4
   */
  setTenantId(tenantId: string | null): void {
    this.tenantId = tenantId;
  }

  /**
   * Obtém um novo token de acesso OAuth2
   */
  private async getOAuthToken(): Promise<string> {
    const { oauth } = environment;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams({
      grant_type: oauth.grantType,
      username: oauth.username,
      password: oauth.password,
    }).toString();

    try {
      const response = await this.http
        .post<OAuthTokenResponse>(oauth.tokenUrl, body, { headers })
        .toPromise();

      if (response && response.access_token) {
        this.accessToken = response.access_token;
        // Define expiração do token (expires_in em segundos, convertido para timestamp)
        this.tokenExpiration = Date.now() + response.expires_in * 1000 - 30000; // 30s de margem
        return this.accessToken;
      } else {
        throw new Error('Resposta inválida do servidor OAuth2');
      }
    } catch (error: any) {
      console.error('Erro ao obter token OAuth2:', error);

      // Se o endpoint OAuth2 não existir, vamos tentar usar autenticação básica
      if (error.status === 404 || error.status === 405) {
        return this.getBasicAuthToken();
      }

      // Se for erro de rede/CORS, usar autenticação básica
      if (error.status === 0 || error.statusText === 'Unknown Error') {
        return this.getBasicAuthToken();
      }

      throw error;
    }
  }

  /**
   * Fallback para autenticação básica se OAuth2 não estiver disponível
   */
  private async getBasicAuthToken(): Promise<string> {
    // Retorna um token dummy para manter compatibilidade
    this.accessToken = 'basic-auth-fallback';
    this.tokenExpiration = Date.now() + 3600000; // 1 hora
    return this.accessToken;
  }

  /**
   * Verifica se o token atual é válido
   */
  private isTokenValid(): boolean {
    return (
      this.accessToken !== null &&
      this.tokenExpiration !== null &&
      Date.now() < this.tokenExpiration
    );
  }

  /**
   * Obtém um token válido (renovando se necessário)
   */
  private async getValidToken(): Promise<string> {
    // Se já está renovando token, aguarda
    if (this.isRefreshing) {
      // Implementação simples: aguarda um pouco e tenta novamente
      await new Promise((resolve) => setTimeout(resolve, 100));
      return this.getValidToken();
    }

    // Se temos um token externo válido, usa ele
    if (this.externalToken && this.isTokenValid()) {
      return this.accessToken!;
    }

    // Se o token é válido, retorna
    if (this.isTokenValid()) {
      return this.accessToken!;
    }

    // Precisa renovar o token
    this.isRefreshing = true;
    try {
      const token = await this.getOAuthToken();
      this.isRefreshing = false;
      return token;
    } catch (error) {
      this.isRefreshing = false;
      throw error;
    }
  }

  private buildApiHeaders(authorization: string): HttpHeaders {
    const headers: Record<string, string> = {
      Authorization: authorization,
      'Content-Type': 'application/json',
    };

    if (this.tenantId) {
      headers['tenantId'] = this.tenantId;
    }

    return new HttpHeaders(headers);
  }

  /**
   * Cria headers com autenticação Bearer ou Basic
   */
  public async createAuthHeaders(): Promise<HttpHeaders> {
    const token = await this.getValidToken();

    // Se for o token de fallback, usar autenticação básica
    if (token === 'basic-auth-fallback') {
      return this.buildApiHeaders('BASIC YWRtaW46UHJlY2sxOTAqKg==');
    }

    return this.buildApiHeaders(`Bearer ${token}`);
  }

  /**
   * Método GET com autenticação OAuth2
   */
  async get(path: string, params?: any): Promise<ResultHttpInterface> {
    const { urlApi } = environment;

    try {
      const headers = await this.createAuthHeaders();

      const res = await this.http
        .get(`${urlApi}/${path}`, {
          headers,
          params,
        })
        .toPromise();

      return { success: true, data: res, error: undefined };
    } catch (e: any) {
      console.error(`Erro na requisição GET ${path}:`, e);
      console.error('Status:', e.status, 'StatusText:', e.statusText);
      console.error('URL:', `${urlApi}/${path}`);

      // Se erro 401, limpa token e tenta renovar uma vez
      if (e.status === 401 && this.accessToken) {
        console.log('Token expirado, tentando renovar...');
        this.accessToken = null;
        this.tokenExpiration = null;

        try {
          const headers = await this.createAuthHeaders();
          const res = await this.http
            .get(`${urlApi}/${path}`, {
              headers,
              params,
            })
            .toPromise();

          return { success: true, data: res, error: undefined };
        } catch (retryError: any) {
          console.error(`Erro na segunda tentativa GET ${path}:`, retryError);
          if (retryError.status === 401) {
            this.router.navigate(['/login']);
          }
          throw retryError;
        }
      }

      if (e.status === 401) {
        this.router.navigate(['/login']);
      }
      throw e;
    }
  }

  /**
   * Método POST com autenticação OAuth2
   */
  async post(
    path: string,
    body: any,
    params?: any,
  ): Promise<ResultHttpInterface> {
    const { urlApi } = environment;

    try {
      const headers = await this.createAuthHeaders();

      const res = await this.http
        .post(`${urlApi}/${path}`, body, {
          headers,
          params,
        })
        .toPromise();

      return { success: true, data: res, error: undefined };
    } catch (e: any) {
      console.error(`Erro na requisição POST ${path}:`, e);
      console.error('Status:', e.status, 'StatusText:', e.statusText);
      console.error('URL:', `${urlApi}/${path}`);

      // Se erro 401, limpa token e tenta renovar uma vez
      if (e.status === 401 && this.accessToken) {
        console.log('Token expirado, tentando renovar...');
        this.accessToken = null;
        this.tokenExpiration = null;

        try {
          const headers = await this.createAuthHeaders();
          const res = await this.http
            .post(`${urlApi}/${path}`, body, {
              headers,
              params,
            })
            .toPromise();

          return { success: true, data: res, error: undefined };
        } catch (retryError: any) {
          console.error(`Erro na segunda tentativa POST ${path}:`, retryError);
          if (retryError.status === 401) {
            this.router.navigate(['/login']);
          }
          throw retryError;
        }
      }

      if (e.status === 401) {
        this.router.navigate(['/login']);
      }
      throw e;
    }
  }

  /**
   * Limpa o token armazenado (útil para logout)
   */
  clearToken(): void {
    this.accessToken = null;
    this.tokenExpiration = null;
    this.externalToken = null;
  }

  /**
   * Testa a conectividade com a API
   */
  async testConnection(): Promise<boolean> {
    try {
      const headers = await this.createAuthHeaders();
      return true;
    } catch (error: any) {
      console.error('Erro ao testar conectividade:', error);
      return false;
    }
  }
}
